import { useCallback, useMemo, useState } from 'react'

import { AthenaError } from '../lib/api/client.ts'
import { describeError, fieldOfError } from '../lib/api/messages.ts'
import { machines } from '../lib/api/resources.ts'
import { admin } from '../lib/api/admin.ts'
import type { AgentCommand } from '../lib/api/admin.ts'
import type { MachineResponse } from '../lib/api/types.ts'
import { useAuth } from '../lib/auth/AuthContext.tsx'
import {
  latestUninstallByEndpoint,
  MACHINE_UNINSTALL_WARNING,
  UNINSTALL_KIND,
} from '../lib/commands/uninstall.ts'
import { formatDayClock } from '../lib/format.ts'
import { Link, navigate } from '../lib/router.tsx'
import { useAction } from '../lib/useAction.ts'
import { useDebounced } from '../lib/useDebounced.ts'
import { useResource } from '../lib/useResource.ts'
import { CompanySelect, useCompanyList } from '../components/companies/CompanySelect.tsx'
import { PageHeader, Toolbar } from '../components/layout/PageHeader.tsx'
import { Badge } from '../components/primitives/Badge.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { ShortId } from '../components/primitives/CopyValue.tsx'
import { ConfirmDialog, FormDialog } from '../components/primitives/Dialog.tsx'
import { FilterToggle, SearchInput, SelectField, TextField } from '../components/primitives/Field.tsx'
import { MachineArtifactsDialog } from '../components/machines/MachineArtifacts.tsx'
import { MachineIpsDialog } from '../components/machines/MachineIps.tsx'
import { MachineIsolationPolicyDialog } from '../components/machines/IsolationPolicy.tsx'
import { MachineWebExceptionsDialog } from '../components/machines/WebExceptions.tsx'
import {
  IsolationBadge,
  machineLabel,
  MachineCommandsDialog,
  ReleaseHostDialog,
} from '../components/machines/ReleaseHost.tsx'
import {
  MachineUninstallDialog,
  UninstallBadge,
  UninstallWarning,
} from '../components/machines/UninstallAgent.tsx'
import { Menu, MenuItem, MenuSeparator } from '../components/primitives/Menu.tsx'
import { Pagination } from '../components/primitives/Pagination.tsx'
import { EmptyState, ErrorState, LoadingRow } from '../components/primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../components/primitives/table.ts'
import { useToast } from '../components/primitives/Toast.tsx'

const PAGE_SIZE = 50

/** Teto de página do Athena: as desinstalações mais recentes da frota, numa chamada só. */
const UNINSTALL_WINDOW = 200

type Dialog =
  | { kind: 'create' }
  | { kind: 'rename'; machine: MachineResponse }
  | { kind: 'edit'; machine: MachineResponse }
  | { kind: 'delete'; machine: MachineResponse }
  | { kind: 'release'; machine: MachineResponse; track: AgentCommand | null }
  | { kind: 'commands'; machine: MachineResponse }
  | { kind: 'ips'; machine: MachineResponse }
  | { kind: 'artifacts'; machine: MachineResponse }
  | { kind: 'webExceptions'; machine: MachineResponse }
  | { kind: 'isolationPolicy'; machine: MachineResponse }
  | { kind: 'uninstall'; machine: MachineResponse; track: AgentCommand | null }
  | null

type IsolationFilter = '' | 'true' | 'false'

const ISOLATION_OPTIONS: ReadonlyArray<{ value: IsolationFilter; label: string }> = [
  { value: '', label: 'todas' },
  { value: 'true', label: 'só isoladas' },
  { value: 'false', label: 'só não isoladas' },
]

/**
 * Máquinas.
 *
 * O `id` da linha é o `endpoint_id` da telemetria daquela máquina.
 *
 * **O fingerprint preserva a caixa.** `PF2X8K91` e `pf2x8k91` são máquinas
 * diferentes para o servidor, e por isso nada aqui faz `toUpperCase()` antes de
 * enviar: normalizar no cliente criaria uma máquina que o operador não consegue
 * encontrar depois. A busca da listagem, essa sim, ignora maiúsculas — busca é
 * busca, identidade é identidade.
 */
export function Machines() {
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const search = useDebounced(searchInput)
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [isolated, setIsolated] = useState<IsolationFilter>('')
  const [companyId, setCompanyId] = useState('')
  const [dialog, setDialog] = useState<Dialog>(null)
  const { isAdmin } = useAuth()
  const companyList = useCompanyList()

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      machines.list(
        {
          page,
          pageSize: PAGE_SIZE,
          search,
          includeDeleted,
          isolated: isolated === '' ? undefined : isolated === 'true',
          // Máquina "da empresa": agente dela na máquina ou licença viva (§3.4).
          companyId: companyId === '' ? undefined : companyId,
        },
        signal,
      ),
    [page, search, includeDeleted, isolated, companyId],
  )

  const { data, error, loading, reload } = useResource(fetcher)

  // O selo de desinstalação na lista sai de UMA leitura da frota (`kind=uninstall_agent`),
  // não de uma chamada por linha. Limitação: só as UNINSTALL_WINDOW desinstalações mais
  // recentes da frota ganham selo; o diálogo da máquina lê a dela sempre.
  const uninstallFetcher = useCallback(
    (signal: AbortSignal) => admin.commands({ kind: UNINSTALL_KIND, pageSize: UNINSTALL_WINDOW }, signal),
    [],
  )
  const uninstalls = useResource(uninstallFetcher)
  const reloadUninstalls = uninstalls.reload
  const uninstallIndex = useMemo(
    () => latestUninstallByEndpoint(uninstalls.data?.items ?? []),
    [uninstalls.data],
  )

  const close = useCallback(() => setDialog(null), [])
  const refresh = useCallback(() => {
    setDialog(null)
    reload()
    // Remover a máquina enfileira a desinstalação: o selo precisa aparecer.
    reloadUninstalls()
  }, [reload, reloadUninstalls])
  const reloadAll = useCallback(() => {
    reload()
    reloadUninstalls()
  }, [reload, reloadUninstalls])

  return (
    <>
      <PageHeader
        title="Máquinas"
        actions={
          <Button variant="primary" onClick={() => setDialog({ kind: 'create' })}>
            Nova máquina
          </Button>
        }
      />

      <Toolbar>
        <SearchInput
          label="Buscar máquina"
          placeholder="trecho do fingerprint"
          value={searchInput}
          onValueChange={(value) => {
            setSearchInput(value)
            setPage(1)
          }}
          className="w-64"
        />
        <FilterToggle
          label="removidas"
          pressed={includeDeleted}
          title="Incluir máquinas removidas logicamente"
          onPressedChange={(value) => {
            setIncludeDeleted(value)
            setPage(1)
          }}
        />
        <SelectField
          label="Rede"
          value={isolated}
          onValueChange={(value) => {
            setIsolated(value as IsolationFilter)
            setPage(1)
          }}
          options={ISOLATION_OPTIONS}
          className="w-40"
        />
        <CompanySelect
          list={companyList}
          value={companyId}
          onChange={(value) => {
            setCompanyId(value)
            setPage(1)
          }}
          allLabel="Todas as empresas"
        />
        {loading && <span className="text-micro text-fg-muted">carregando…</span>}
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-y-auto pt-2">
        {error !== null ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data === null ? (
          <LoadingRow />
        ) : data.items.length === 0 ? (
          <EmptyState
            title="Nenhuma máquina encontrada."
            hint={search === '' ? undefined : `Nada corresponde a “${search}”.`}
            action={
              search === '' ? (
                <Button onClick={() => setDialog({ kind: 'create' })}>Nova máquina</Button>
              ) : undefined
            }
          />
        ) : (
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>
                  Nome
                </th>
                <th scope="col" className={TH}>
                  Fingerprint
                </th>
                <th scope="col" className={TH}>
                  endpoint_id
                </th>
                <th scope="col" className={TH}>
                  Situação
                </th>
                <th scope="col" className={TH}>
                  Criada
                </th>
                <th scope="col" className={`${TH} text-right`}>
                  <span className="sr-only">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((machine) => (
                <tr key={machine.id} className={TR}>
                  <th scope="row" className={`${TD} max-w-0 font-normal`}>
                    {machine.displayName === null ? (
                      <span className="text-fg-muted italic">sem nome</span>
                    ) : (
                      <span className="block truncate text-fg" title={machine.displayName}>
                        {machine.displayName}
                      </span>
                    )}
                  </th>
                  <td className={`${TD} max-w-0`}>
                    <span
                      className="block truncate font-mono text-fg-muted"
                      title={machine.fingerprint}
                    >
                      {machine.fingerprint}
                    </span>
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>
                    <ShortId id={machine.id} label="endpoint_id" />
                  </td>
                  <td className={TD}>
                    <span className="flex flex-wrap items-center gap-1.5">
                      {machine.deletedAt === null ? (
                        <Badge tone="neutral">cadastrada</Badge>
                      ) : (
                        <Badge tone="crit">removida</Badge>
                      )}
                      <IsolationBadge machine={machine} />
                      <UninstallCell
                        command={uninstallIndex.get(machine.id)}
                        onOpen={() => setDialog({ kind: 'uninstall', machine, track: null })}
                      />
                      {/* Só com o host isolado. Staff vê o botão desabilitado, com o
                          motivo — o 403 do servidor continua tratado no diálogo. */}
                      {machine.isolation?.is_isolated === true && machine.deletedAt === null && (
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={!isAdmin}
                          title={isAdmin ? undefined : 'Exige o papel de administrador.'}
                          onClick={() => setDialog({ kind: 'release', machine, track: null })}
                        >
                          Desbloquear
                        </Button>
                      )}
                    </span>
                  </td>
                  <td className={`${TD} tnum whitespace-nowrap`}>
                    {formatDayClock(machine.createdAt)}
                  </td>
                  <td className={`${TD} text-right`}>
                    {machine.deletedAt === null && (
                      <Menu trigger={<span>ações</span>} label={`Ações de ${machine.fingerprint}`}>
                        <MenuItem onSelect={() => setDialog({ kind: 'rename', machine })}>
                          Renomear
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'edit', machine })}>
                          Editar fingerprint
                        </MenuItem>
                        <MenuItem onSelect={() => navigate(`/licencas?machineId=${machine.id}`)}>
                          Ver licenças
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'isolationPolicy', machine })}>
                          Regime de isolamento
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'ips', machine })}>
                          IPs (firewall)
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'webExceptions', machine })}>
                          Exceções web
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'artifacts', machine })}>
                          Artefatos
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'commands', machine })}>
                          Histórico de comandos
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'uninstall', machine, track: null })}>
                          Desinstalação do agente
                        </MenuItem>
                        <MenuSeparator />
                        <MenuItem danger onSelect={() => setDialog({ kind: 'delete', machine })}>
                          Remover
                        </MenuItem>
                      </Menu>
                    )}
                    {/* Removida: é aqui que o operador confere se o agente saiu da máquina. */}
                    {machine.deletedAt !== null && (
                      <Menu trigger={<span>ações</span>} label={`Ações de ${machine.fingerprint}`}>
                        <MenuItem onSelect={() => setDialog({ kind: 'uninstall', machine, track: null })}>
                          Desinstalação do agente
                        </MenuItem>
                        <MenuItem onSelect={() => setDialog({ kind: 'commands', machine })}>
                          Histórico de comandos
                        </MenuItem>
                      </Menu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data !== null && data.items.length > 0 && (
        <Pagination
          page={data.page}
          pageSize={data.pageSize}
          totalCount={data.totalCount}
          totalPages={data.totalPages}
          busy={loading}
          onPageChange={setPage}
        />
      )}

      {dialog?.kind === 'create' && <CreateDialog onClose={close} onCreated={refresh} />}

      {dialog?.kind === 'rename' && (
        <RenameDialog machine={dialog.machine} onClose={close} onSaved={refresh} />
      )}

      {dialog?.kind === 'edit' && (
        <EditDialog machine={dialog.machine} onClose={close} onSaved={refresh} />
      )}

      {dialog?.kind === 'release' && (
        // `reload`, não `refresh`: a lista se atualiza sem fechar o acompanhamento.
        <ReleaseHostDialog machine={dialog.machine} track={dialog.track} onClose={close} onReleased={reload} />
      )}

      {dialog?.kind === 'ips' && (
        <MachineIpsDialog
          machine={{ id: dialog.machine.id, label: machineLabel(dialog.machine) }}
          onClose={close}
        />
      )}

      {/* Uma leitura por máquina, só ao abrir: a lista não consulta o regime linha a linha. */}
      {dialog?.kind === 'isolationPolicy' && (
        <MachineIsolationPolicyDialog
          machine={{ id: dialog.machine.id, label: machineLabel(dialog.machine) }}
          onClose={close}
        />
      )}

      {dialog?.kind === 'webExceptions' && (
        <MachineWebExceptionsDialog
          machine={{ id: dialog.machine.id, label: machineLabel(dialog.machine) }}
          onClose={close}
        />
      )}

      {dialog?.kind === 'artifacts' && (
        <MachineArtifactsDialog
          machine={{ id: dialog.machine.id, label: machineLabel(dialog.machine) }}
          onClose={close}
        />
      )}

      {dialog?.kind === 'commands' && (
        <MachineCommandsDialog
          machine={dialog.machine}
          onClose={close}
          onTrack={(command) => setDialog({ kind: 'release', machine: dialog.machine, track: command })}
          onTrackUninstall={(command) => setDialog({ kind: 'uninstall', machine: dialog.machine, track: command })}
        />
      )}

      {dialog?.kind === 'uninstall' && (
        // `reload`, não `refresh`: a lista se atualiza sem fechar o acompanhamento.
        <MachineUninstallDialog machine={dialog.machine} track={dialog.track} onClose={close} onChanged={reloadAll} />
      )}

      {dialog?.kind === 'delete' && (
        <DeleteDialog machine={dialog.machine} onClose={close} onDeleted={refresh} />
      )}
    </>
  )
}

/** Selo da última desinstalação da máquina; abre o andamento dela. */
function UninstallCell({ command, onOpen }: { command: AgentCommand | undefined; onOpen: () => void }) {
  if (command === undefined) return null
  return (
    <button type="button" className="inline-flex" onClick={onOpen}>
      <UninstallBadge command={command} />
    </button>
  )
}

const FINGERPRINT_HINT =
  'Diferencia maiúsculas de minúsculas.'

function CreateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast()
  const action = useAction()
  const [fingerprint, setFingerprint] = useState('')

  const field = fieldOfError(action.error)

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Nova máquina"
      submitLabel="Cadastrar"
      busy={action.busy}
      error={field === null && action.error !== null ? describeError(action.error) : null}
      onSubmit={() =>
        action.run(async () => {
          // Enviado exatamente como digitado — sem toUpperCase, sem trim.
          const created = await machines.create({ fingerprint })
          toast.success(`Máquina ${created.fingerprint} cadastrada.`)
          onCreated()
        })
      }
    >
      <TextField
        label="Fingerprint"
        value={fingerprint}
        onValueChange={setFingerprint}
        error={field === 'fingerprint' ? describeError(action.error) : null}
        hint={FINGERPRINT_HINT}
        maxLength={256}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        required
        mono
        autoFocus
      />
    </FormDialog>
  )
}

const DISPLAY_NAME_HINT =
  'Em branco, volta ao hostname.'

function RenameDialog({
  machine,
  onClose,
  onSaved,
}: {
  machine: MachineResponse
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const [displayName, setDisplayName] = useState(machine.displayName ?? '')

  const field = fieldOfError(action.error)

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Renomear máquina"
      submitLabel="Salvar"
      busy={action.busy}
      error={field === null && action.error !== null ? describeError(action.error) : null}
      onSubmit={() =>
        action.run(async () => {
          // Vazio limpa o apelido no servidor: o rótulo cai de volta para o hostname.
          const trimmed = displayName.trim()
          const saved = await machines.setDisplayName(machine.id, {
            displayName: trimmed === '' ? null : trimmed,
          })
          toast.success(
            saved.displayName === null
              ? 'Nome de exibição removido.'
              : `Máquina renomeada para “${saved.displayName}”.`,
          )
          onSaved()
        })
      }
    >
      <TextField
        label="Nome de exibição"
        value={displayName}
        onValueChange={setDisplayName}
        error={field === 'displayName' ? describeError(action.error) : null}
        hint={DISPLAY_NAME_HINT}
        maxLength={128}
        placeholder="ex.: Recepção — PDV 2"
        autoFocus
      />
    </FormDialog>
  )
}

function EditDialog({
  machine,
  onClose,
  onSaved,
}: {
  machine: MachineResponse
  onClose: () => void
  onSaved: () => void
}) {
  const toast = useToast()
  const action = useAction()
  const [fingerprint, setFingerprint] = useState(machine.fingerprint)

  const field = fieldOfError(action.error)

  return (
    <FormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Editar fingerprint"
      submitLabel="Salvar"
      busy={action.busy}
      error={field === null && action.error !== null ? describeError(action.error) : null}
      onSubmit={() =>
        action.run(async () => {
          const saved = await machines.update(machine.id, { fingerprint })
          toast.success(`Máquina ${saved.fingerprint} atualizada.`)
          onSaved()
        })
      }
    >
      <TextField
        label="Fingerprint"
        value={fingerprint}
        onValueChange={setFingerprint}
        error={field === 'fingerprint' ? describeError(action.error) : null}
        hint={FINGERPRINT_HINT}
        maxLength={256}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        required
        mono
        autoFocus
      />
    </FormDialog>
  )
}

/** O que remover a máquina faz — inclusive com o agente instalado nela. */
export function DeleteMachineConsequence({ machine }: { machine: MachineResponse }) {
  return (
    <>
      <span className="font-mono text-fg">{machine.fingerprint}</span> sai das listagens.
      <UninstallWarning>{MACHINE_UNINSTALL_WARNING}</UninstallWarning>
    </>
  )
}

function DeleteDialog({
  machine,
  onClose,
  onDeleted,
}: {
  machine: MachineResponse
  onClose: () => void
  onDeleted: () => void
}) {
  const toast = useToast()
  const action = useAction()

  const blocked =
    action.error instanceof AthenaError && action.error.code === 'machine_has_live_product_keys'

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title="Remover máquina?"
      consequence={<DeleteMachineConsequence machine={machine} />}
      confirmLabel="Remover"
      busy={action.busy}
      error={
        blocked ? (
          <span>
            {describeError(action.error)}{' '}
            <Link href={`/licencas?machineId=${machine.id}`} className="underline">
              Ver licenças desta máquina
            </Link>
          </span>
        ) : action.error !== null ? (
          describeError(action.error)
        ) : null
      }
      onConfirm={() =>
        action.run(async () => {
          await machines.remove(machine.id)
          // A desinstalação sai do Athena; 204 não diz se havia agente ativo.
          toast.success('Máquina removida. Com agente ativo, ele se desinstala quando conectar.')
          onDeleted()
        })
      }
    />
  )
}
